/**
 * Poprawki tekstu a późniejsze zmiany w formularzu.
 *
 * Przy poprawce zapisujemy tekst wygenerowany wtedy z formularza (`textBase`). Gdy dane w formularzu
 * się zmienią, łączymy oba źródła:
 * - pole wstawiane z formularza, które się zmieniło (np. „Kim była”, data mszy), zawsze bierzemy na nowo
 *   z formularza - poprawki w tym miejscu i tuż przy nim są nadpisywane (pole oznaczamy jako konflikt,
 *   żeby w „Edytuj tekst” było widać, co sprawdzić),
 * - poprawki w pozostałych miejscach napisu (stały tekst, niezmienione pola) zostają.
 *
 * Porównujemy po słowach.
 */

import { parseTextDSL } from '../layouts/textDSL/parser';
import { parseTextDSLWithSegments } from '../layouts/textDSL/segments';

const TOKEN_REGEX = /\s+|[\p{L}\p{N}]+|[^\s\p{L}\p{N}]/gu;

const tokenize = text => text.match(TOKEN_REGEX) || [];

/**
 * Zmiany z `from` do `to` jako fragmenty: tokeny [start, end) z `from` zastąpione tokenami [gStart, gEnd) z `to`.
 * `matchTo[j]` - indeks tokenu z `from`, któremu odpowiada niezmieniony token `to[j]` (albo -1).
 */
function diffTokens(from, to) {
    const n = from.length;
    const m = to.length;
    // lcs[i][j] - najdłuższy wspólny podciąg from[i..] i to[j..]
    const lcs = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
    for (let i = n - 1; i >= 0; i -= 1) {
        for (let j = m - 1; j >= 0; j -= 1) {
            lcs[i][j] = from[i] === to[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
        }
    }

    const hunks = [];
    const matchTo = new Int32Array(m).fill(-1);
    let hunk = null;
    let i = 0;
    let j = 0;
    while (i < n || j < m) {
        if (i < n && j < m && from[i] === to[j]) {
            matchTo[j] = i;
            hunk = null;
            i += 1;
            j += 1;
        } else {
            if (!hunk) {
                hunk = { start: i, end: i, gStart: j, gEnd: j };
                hunks.push(hunk);
            }
            if (j < m && (i === n || lcs[i][j + 1] >= lcs[i + 1][j])) {
                j += 1;
                hunk.gEnd = j;
            } else {
                i += 1;
                hunk.end = i;
            }
        }
    }
    return { hunks, matchTo };
}

/**
 * Zmiany formularza rozszerzone do całych pól: jeśli pole się zmieniło, podmieniamy całe pole,
 * a nie tylko różniące się słowa. Takie fragmenty mają `field: true`.
 */
function expandToFields({ hunks, matchTo }, generatedTokens, tokenFields) {
    if (!tokenFields) return hunks;

    const changedFields = new Set();
    hunks.forEach((hunk) => {
        // Usunięty fragment nie ma własnych tokenów - patrzymy na sąsiadów w nowym tekście
        const from = hunk.gEnd > hunk.gStart ? hunk.gStart : hunk.gStart - 1;
        const to = hunk.gEnd > hunk.gStart ? hunk.gEnd : hunk.gStart + 1;
        for (let g = Math.max(0, from); g < Math.min(to, generatedTokens.length); g += 1) {
            if (tokenFields[g]) changedFields.add(tokenFields[g]);
        }
    });
    if (changedFields.size === 0) return hunks;

    const hunkContaining = g => hunks.find(hunk => g >= hunk.gStart && g < hunk.gEnd);
    const candidates = [...hunks];
    changedFields.forEach((field) => {
        let gStart = -1;
        let gEnd = -1;
        tokenFields.forEach((tokenField, g) => {
            if (tokenField !== field) return;
            if (gStart === -1) gStart = g;
            gEnd = g + 1;
        });
        const start = matchTo[gStart] >= 0 ? matchTo[gStart] : hunkContaining(gStart).start;
        const end = matchTo[gEnd - 1] >= 0 ? matchTo[gEnd - 1] + 1 : hunkContaining(gEnd - 1).end;
        candidates.push({ start, end, gStart, gEnd, field: true });
    });

    // Łączymy fragmenty nachodzące na siebie lub stykające się (w obu tekstach)
    candidates.sort((a, b) => a.start - b.start || a.gStart - b.gStart);
    const merged = [];
    candidates.forEach((hunk) => {
        const last = merged[merged.length - 1];
        if (last && hunk.start <= last.end && hunk.gStart <= last.gEnd) {
            last.end = Math.max(last.end, hunk.end);
            last.gEnd = Math.max(last.gEnd, hunk.gEnd);
            last.field = last.field || hunk.field;
        } else {
            merged.push({ ...hunk });
        }
    });
    return merged;
}

// Fragmenty nachodzą na siebie (dopisanie w tym samym miejscu albo wewnątrz zmienianego fragmentu też).
// Przy zmienionym polu liczy się też dopisanie tuż przy nim.
const overlaps = (a, b) => {
    if (a.field || b.field) return a.start <= b.end && b.start <= a.end;
    if (a.start === a.end && b.start === b.end) return a.start === b.start;
    if (a.start === a.end) return a.start > b.start && a.start < b.end;
    if (b.start === b.end) return b.start > a.start && b.start < a.end;
    return a.start < b.end && b.start < a.end;
};

const applyHunks = (baseTokens, start, end, hunks) => {
    let result = '';
    let position = start;
    hunks.forEach((hunk) => {
        result += baseTokens.slice(position, hunk.start).join('') + hunk.tokens.join('');
        position = hunk.end;
    });
    return result + baseTokens.slice(position, end).join('');
};

/**
 * @param {string} base - tekst z formularza w chwili poprawki
 * @param {string} edited - tekst poprawiony przez użytkownika
 * @param {string} generated - aktualny tekst z formularza
 * @param {(string|null)[]} [tokenFields] - dla każdego słowa z `generated`: pole formularza, z którego pochodzi
 * @returns {{ text: string, conflict: boolean }}
 */
export function mergeEditedText(base, edited, generated, tokenFields = null) {
    if (generated === base || edited === generated) return { text: edited, conflict: false };
    if (edited === base) return { text: generated, conflict: false };

    const baseTokens = tokenize(base);
    const editedTokens = tokenize(edited);
    const generatedTokens = tokenize(generated);
    const withTokens = (tokens, source) => hunk => ({ ...hunk, source, tokens: tokens.slice(hunk.gStart, hunk.gEnd) });
    const all = [
        ...diffTokens(baseTokens, editedTokens).hunks.map(withTokens(editedTokens, 'edited')),
        ...expandToFields(diffTokens(baseTokens, generatedTokens), generatedTokens, tokenFields)
            .map(withTokens(generatedTokens, 'generated')),
    ].sort((a, b) => a.start - b.start || (a.end - a.start) - (b.end - b.start));

    // Grupy nachodzących na siebie zmian - grupa z obu źródeł to konflikt, wygrywa formularz
    const groups = [];
    all.forEach((hunk) => {
        const group = groups[groups.length - 1];
        if (group && group.hunks.some(other => overlaps(other, hunk))) {
            group.hunks.push(hunk);
            group.end = Math.max(group.end, hunk.end);
        } else {
            groups.push({ start: hunk.start, end: hunk.end, hunks: [hunk] });
        }
    });

    let conflict = false;
    let text = '';
    let position = 0;
    groups.forEach((group) => {
        text += baseTokens.slice(position, group.start).join('');
        const editedHunks = group.hunks.filter(hunk => hunk.source === 'edited');
        const generatedHunks = group.hunks.filter(hunk => hunk.source === 'generated');
        if (editedHunks.length && generatedHunks.length) {
            const fromEdited = applyHunks(baseTokens, group.start, group.end, editedHunks);
            const fromGenerated = applyHunks(baseTokens, group.start, group.end, generatedHunks);
            if (fromEdited !== fromGenerated) conflict = true;
            text += fromGenerated;
        } else {
            text += applyHunks(baseTokens, group.start, group.end, group.hunks);
        }
        position = group.end;
    });
    text += baseTokens.slice(position).join('');

    return { text, conflict };
}

/** Dla każdego słowa tekstu z szablonu: klucz pola formularza, z którego pochodzi (albo null) */
function getTokenFields(template, data, generated) {
    const segments = parseTextDSLWithSegments(template, data);
    const owners = [];
    segments.forEach((segment) => {
        const owner = segment.kind === 'field' ? segment.key : null;
        for (let i = 0; i < segment.text.length; i += 1) owners.push(owner);
    });
    // Mapa musi dokładnie pasować do tekstu - inaczej łączymy bez wiedzy o polach
    if (segments.map(segment => segment.text).join('') !== generated) return null;

    const fields = [];
    let offset = 0;
    tokenize(generated).forEach((token) => {
        fields.push(owners.slice(offset, offset + token.length).find(Boolean) || null);
        offset += token.length;
    });
    return fields;
}

/**
 * Poprawki tekstu dopasowane do aktualnych danych formularza.
 * Poprawki bez zapisanego tekstu bazowego (starsze zapisy, dodane napisy) zostają bez zmian.
 *
 * @returns {{ overrides: object, base: object, conflicts: Set<string> }}
 */
export function rebaseTextOverrides({ layout, data, textOverrides, textBase }) {
    const overrides = { ...(textOverrides || {}) };
    const base = { ...(textBase || {}) };
    const conflicts = new Set();
    if (!layout || !data) return { overrides, base, conflicts };

    (layout.elements || []).forEach((element) => {
        const edited = overrides[element.id];
        const savedBase = base[element.id];
        if (element.type !== 'text' || typeof edited !== 'string' || typeof savedBase !== 'string') return;

        const content = element.content || '';
        const generated = parseTextDSL(content, data);
        if (generated === savedBase) return;

        const merged = mergeEditedText(savedBase, edited, generated, getTokenFields(content, data, generated));
        if (merged.conflict) conflicts.add(element.id);
        if (merged.text === generated) {
            delete overrides[element.id];
            delete base[element.id];
        } else {
            overrides[element.id] = merged.text;
            base[element.id] = generated;
        }
    });

    return { overrides, base, conflicts };
}
